import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  Auth0Res,
  Auth0Role,
  Auth0User,
  CreateAuth0UserDTO,
  ListAuth0Role,
  ListAuth0User,
  UpdateUserCountryDTO,
} from '../../pages/auth/interfaces/auth0.interface';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class Auth0Service {
  env = environment;
  baseUrl = this.env.endpoint.multiuse;

  private readonly http = inject(HttpClient);

  getAuth0Token(): Observable<Auth0Res> {
    const { auth } = this.env;
    const params = {
      client_id: environment.qtcAuth0.clientId,
      client_secret: environment.qtcAuth0.clientSecret,
      audience: environment.qtcAuth0.audience,
      grant_type: 'client_credentials',
    };

    return this.http.post<Auth0Res>(
      `https://${auth.domain}/oauth/token`,
      params,
    );
  }

  // createRole(dto: CreateRoleDTO): Observable<Auth0Role> {
  //   return this.http.post<Auth0Role>(`${this.baseUrl}/auth0/roles`, dto);
  // }
  //
  // addRolePermissions(roleId: string, dto: AddPermissionDTO): Observable<void> {
  //   return this.http.post<any>(`${this.baseUrl}/auth0/roles`, dto);
  // }

  getRoles(params?: {
    page?: number;
    per_page?: number;
  }): Observable<ListAuth0Role> {
    let httpParams = new HttpParams();
    if (params?.page != null) {
      httpParams = httpParams.set('page', String(params.page));
    }

    if (params?.per_page != null) {
      httpParams = httpParams.set('per_page', String(params.per_page));
    }

    return this.http.get<ListAuth0Role>(`${this.baseUrl}/auth0/roles`, {
      params: httpParams,
    });
  }

  getRoleById(id: string): Observable<Auth0Role> {
    return this.http.get<Auth0Role>(`${this.baseUrl}/auth0/roles/${id}`);
  }

  updateRole(
    id: string,
    dto: {
      name?: string;
      description?: string;
    },
  ): Observable<Auth0Role> {
    return this.http.patch<Auth0Role>(`${this.baseUrl}/auth0/roles/${id}`, dto);
  }

  listRoleUsers(
    roleId: string,
    params?: { page?: number; per_page?: number },
  ): Observable<Auth0User[]> {
    let httpParams = new HttpParams();
    if (params?.page != null) {
      httpParams = httpParams.set('page', String(params.page));
    }

    if (params?.per_page != null) {
      httpParams = httpParams.set('per_page', String(params.per_page));
    }

    return this.http.get<Auth0User[]>(
      `${this.baseUrl}/auth0/roles/${roleId}/users`,
      { params: httpParams },
    );
  }

  listUsers(opts?: {
    page?: number;
    per_page?: number;
    q?: string;
    fields?: string;
    include_fields?: boolean;
  }): Observable<ListAuth0User> {
    let params = new HttpParams();
    if (opts?.page != null) {
      params = params.set('page', String(opts.page));
    }

    if (opts?.per_page != null) {
      params = params.set('per_page', String(opts.per_page));
    }

    if (opts?.q) {
      params = params.set('q', opts.q).set('search_engine', 'v3');
    }

    if (opts?.fields) {
      params = params.set('fields', opts.fields);
    }

    if (opts?.include_fields !== undefined) {
      params = params.set('include_fields', String(opts.include_fields));
    }

    return this.http.get<ListAuth0User>(`${this.baseUrl}/auth0/users`, {
      params,
    });
  }

  assignRolesToUser(id: string, roles: string[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth0/users/${id}/roles`, {
      roles,
    });
  }

  deleteRolesFromUser(id: string, roles: string[]): Observable<void> {
    const options = {
      body: { roles },
    };
    return this.http.delete<void>(
      `${this.baseUrl}/auth0/users/${id}/roles`,
      options,
    );
  }

  listUserRoles(id: string): Observable<Auth0Role[]> {
    return this.http.get<Auth0Role[]>(
      `${this.baseUrl}/auth0/users/${id}/roles`,
    );
  }

  getUserById(id: string): Observable<Auth0User> {
    return this.http.get<Auth0User>(`${this.baseUrl}/auth0/users/${id}`);
  }

  createUser(dto: CreateAuth0UserDTO): Observable<Auth0User> {
    return this.http.post<Auth0User>(`${this.baseUrl}/auth0/users`, dto);
  }

  blockUser(id: string): Observable<Auth0User> {
    return this.http.patch<Auth0User>(
      `${this.baseUrl}/auth0/users/${id}/block`,
      { blocked: true },
    );
  }

  unblockUser(id: string): Observable<Auth0User> {
    return this.http.patch<Auth0User>(
      `${this.baseUrl}/auth0/users/${id}/unblock`,
      { blocked: false },
    );
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/auth0/users/${id}`);
  }

  updateUserCountry(
    id: string,
    dto: UpdateUserCountryDTO,
  ): Observable<Auth0User> {
    return this.http.patch<Auth0User>(
      `${this.baseUrl}/auth0/users/${id}/country`,
      dto,
    );
  }

  deleteUserCountry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/auth0/users/${id}/country`);
  }
}
